// La oficina a pantalla completa, como la pantalla de inicio del iPhone: cada agente en su mesa fija y, sobre la mesa,
// lo que le toca a quien mira. Lienzo nítido (zoom entero, Lienzo.tsx) + capa DOM con lo que se lee y se pulsa, por
// capas: cabeceras de sala, carteles de grupo, la franja de los que se van, mesas (libres, con agente) y el fantasma del
// arrastre. Encima, fuera de la capa que se desplaza: la barra de «Ordenar» y los indicadores de borde. Si el mundo no
// cabe a zoom 2, la escena se desplaza y la cámara arranca centrada (useCamara).
//
// 04-11 — grupos de mesas («mesas de equipo», web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md §4): Ctrl/⌘+clic
// elige varias mesas; Ctrl+G agrupa lo elegido (o la mesa con el foco) y abre el nombre en su cartel; Ctrl+Mayús+G
// deshace su grupo. Soltar a un agente en la placa de un grupo lo mete; en «Sin grupo», lo saca; sobre otro agente, se
// cambian el sitio. En «Ordenar» se arrastra el cartel y el grupo entero cambia de orden. La planta no se filtra nunca.
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as EventoTeclado, type PointerEvent as EventoPuntero } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useToastStore } from '../../../stores/toast.store';
import { ApiHttpError } from '../../../lib/api';
import type { OrdenMesas } from '../api';
import type { ContratadoDTO, GrupoDTO, SalaDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { useEsperasDeGrupos, useSantiPorAgente } from '../pelotas/porAgente';
import { usePelotasStore } from '../pelotas/store';
import { useOficinaStore } from '../store';
import { seVa } from '../marco/situacion';
import { escena } from '../ui/paleta.js';
import { useAccionesEstables } from '../../../hooks/useAccionesEstables';
import { BarraOrdenar } from './BarraOrdenar';
import { CartelGrupo, EtiquetaSinGrupo } from './CartelGrupo';
import { Fantasma } from './Fantasma';
import { PARED, type GrupoMundo, type MesaMundo } from './generar';
import { Carpeta } from './Carpeta';
import { claveSitio, desplazarCercaDelBorde, estiloDe, mesaVecina, otraSalaEn, sitioDeClave, sitioEn, type Sitio } from './geometria';
import { destinosDeSala, sugerirGrupo } from './grupos';
import { IndicadoresFuera } from './IndicadoresFuera';
import { Letrero } from './Letrero';
import { resumenLetrero } from './letrero';
import { Lienzo, useFiguras } from './Lienzo';
import { useMarcadores, type MarcadorMundo } from './marcador';
import { MarcaDestino } from './MarcaDestino';
import { MesaAgente } from './MesaAgente';
import type { MoverAGrupo } from './MenuAgente';
import { MesaLibre, MesaVacia } from './MesaLibre';
import { Salida } from './Salida';
import { useArrastreMesas } from './useArrastreMesas';
import { useCamara } from './useCamara';
import { useMundo } from './useMundo';
import { usePulsacionLarga } from './usePulsacionLarga';
import { useTarjeta } from './useTarjeta';

export interface PropsMundo {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  /** 04-11 — los grupos de mesas de las salas (los vacíos no se pintan). */
  grupos?: GrupoDTO[];
  panelIzqPx: number;
  /** Lo que la PLANTA deja libre a la izquierda (si no, `panelIzqPx`): plegar la lista no re-maqueta la oficina. */
  reservaIzqPx?: number;
  /** Agente resaltado desde la lista (pasar el ratón por su fila): su mesa se marca y enseña su tarjeta. */
  resaltado?: string | null;
  /**
   * 04-11 — el agente abierto en el chat (desde la lista, Alt+↓, Ctrl+K o un aviso). Si su sala va plegada en carpeta, la
   * escena la abre; y lleva la vista hasta su mesa (orden.md regla 14), para que al cerrar el chat esté a la vista.
   */
  enfocado?: string | null;
  alAbrir: (agentId: string) => void;
  /** Contratar (en esa sala y, si se pide desde el «+» de un grupo, en ese grupo). */
  alContratar: (salaId?: string, grupoId?: string) => void;
  alAjustarSala: (salaId: string) => void;
  /** Qué enseña cada agente (lo mismo que la lista: «¿qué me toca?») y si es de otra persona. */
  marcador: (c: ContratadoDTO) => MarcadorMundo;
  /** Mover a un agente de mesa o de grupo (arrastrar, «Mover a ▸»; las mesas son automáticas). */
  alOrdenarMesas: (projectId: string, orden: OrdenMesas) => void;
}

const SIN_GRUPOS: GrupoDTO[] = [];
const SIN_GENTE: ContratadoDTO[] = [];

/** ¿Hay algo encima que se queda las teclas (un diálogo, un menú abierto) o se está escribiendo? */
function tecladoOcupado(e: KeyboardEvent): boolean {
  const destino = e.target as HTMLElement | null;
  const escribiendo = !!destino && (destino.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino.tagName));
  return escribiendo || !!document.querySelector('[role="dialog"], [role="menu"]') || useOficinaStore.getState().chatAbierto !== null;
}

export const Mundo = memo(function Mundo({ salas, contratados, grupos = SIN_GRUPOS, panelIzqPx, reservaIzqPx, resaltado, enfocado, alAbrir, alContratar, alAjustarSala, marcador, alOrdenarMesas }: PropsMundo) {
  const marco = useRef<HTMLDivElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const capa = useRef<HTMLDivElement>(null);
  const lista = useMemo(() => Object.values(contratados), [contratados]);
  // 04-11 — salas = carpetas (orden.md regla 12): la secundaria que Pau abrió (generar.ts decide si hay que plegar) y la
  // que se acaba de abrir, para su animación de 250 ms.
  const [abierta, setAbierta] = useState<string | null>(null);
  const [desplegando, setDesplegando] = useState<string | null>(null);
  const { mundo, hueco } = useMundo(marco, salas, lista, reservaIzqPx ?? panelIzqPx, grupos, abierta);
  const marcas = useMarcadores(lista, marcador);
  const figuras = useFiguras(lista, marcas);
  const santi = useSantiPorAgente();
  const esperas = useEsperasDeGrupos();
  const tablero = usePelotasStore((s) => s.lista);
  const contratos = tablero?.configurado ? tablero.contratos : null;
  const tarjeta = useTarjeta();
  const [menu, setMenu] = useState<string | null>(null);
  // 04-11 — mesas elegidas con Ctrl/⌘+clic (siempre de una sala) y el grupo cuyo nombre se está escribiendo.
  const [eleccion, setEleccion] = useState<{ projectId: string; ids: ReadonlySet<string> } | null>(null);
  // `antes` (solo al crear con Ctrl+G o «Grupo nuevo…»): de dónde venía cada agente, para devolverlo si Pau cancela el nombre.
  const [renombrando, setRenombrando] = useState<{ grupoId: string; nuevo: boolean; antes?: { agentId: string; grupoId: string | null; mesa: number }[] } | null>(null);
  // 04-11 (#269) — el nombre que propone la recepción para el grupo recién creado (llega en unos segundos; el campo lo pone
  // solo si Pau no ha tecleado). Mientras, el campo enseña la sugerencia local al instante (grupos.ts `sugerirGrupo`).
  const [propuesta, setPropuesta] = useState<{ grupoId: string; nombre: string } | null>(null);
  // En «Ordenar»: el grupo que se arrastra por su cartel y sobre qué otro grupo va a caer.
  const [cartelArrastrado, setCartelArrastrado] = useState<{ grupoId: string; sobre: string | null } | null>(null);

  // «Ordenar» es UNO para toda la oficina (orden.md regla 16; antes, uno por sala): tiemblan todas las salas, las mesas
  // vacías se marcan y el dedo arrastra. Cada agente sigue moviéndose solo dentro de su sala (regla 12 de grupos).
  // 05-11 (#278): su botón va en la franja de los widgets (Escenario), lejos de las cabeceras de sala: el estado vive en
  // el almacén de la oficina. Al irse la oficina, se apaga (volver no la encuentra temblando).
  const ordenando = useOficinaStore((s) => s.ordenandoMesas);
  const setOrdenando = useOficinaStore((s) => s.setOrdenandoMesas);
  useEffect(() => () => useOficinaStore.getState().setOrdenandoMesas(false), []);

  const mundoRef = useRef(mundo);
  mundoRef.current = mundo;
  const contratadosRef = useRef(contratados);
  contratadosRef.current = contratados;

  /** Lleva a un agente a un sitio: una mesa de un grupo, o la placa de un grupo (= su primer sitio libre). */
  const llevarA = (projectId: string, agentId: string, sitio: Sitio) => {
    const c = contratadosRef.current[agentId];
    if (!c) return;
    const cambiaGrupo = sitio.grupoId !== (c.grupoId ?? null);
    if (!cambiaGrupo && (sitio.mesa === null || sitio.mesa === c.mesa)) return;
    alOrdenarMesas(projectId, { accion: 'mover', agentId, ...(cambiaGrupo ? { grupoId: sitio.grupoId } : {}), ...(sitio.mesa !== null ? { mesa: sitio.mesa } : {}) });
  };

  const { arrastre, empezar: empezarArrastre, ocupado, acabaDeSoltar } = useArrastreMesas({
    mesaBajo: (x, y, projectId) => {
      const r = capa.current?.getBoundingClientRect();
      const m = mundoRef.current;
      if (!m || !r) return { sobre: null, px: 0, py: 0 };
      return { sobre: sitioEn(m, projectId, x - r.left, y - r.top), px: x - r.left, py: y - r.top };
    },
    alEmpezar: tarjeta.cerrar,
    alMover: (x, y) => caja.current && desplazarCercaDelBorde(caja.current, x, y, panelIzqPx),
    alSoltarEnMesa: (projectId, agentId, clave) => llevarA(projectId, agentId, sitioDeClave(clave)),
    // Regla 12: soltarlo en otra sala no hace nada y lo dice (cada sala es su proyecto).
    alSoltarFuera: (projectId, px, py) => {
      const m = mundoRef.current;
      if (m && otraSalaEn(m, projectId, px, py)) useToastStore.getState().push({ message: t('oficinaV2.grupos.otraSala'), durationMs: 4_000 });
    },
  });
  // Escape cierra «Ordenar», salvo que otra cosa encima ya lo usara (Ctrl+K, un diálogo) o haya un arrastre en marcha
  // (ese lo cancela él).
  useEffect(() => {
    if (!ordenando) return;
    const tecla = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !ev.defaultPrevented && !ocupado() && !document.querySelector('[role="dialog"]')) setOrdenando(false);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [ordenando, ocupado]);

  const nombreSala = useMemo(() => new Map(salas.map((s) => [s.projectId, s.nombre])), [salas]);
  // Los sentados de cada sala (sin los que se van), en su orden: las caras de su carpeta.
  const gentePorSala = useMemo(() => {
    const m = new Map<string, ContratadoDTO[]>();
    for (const c of [...lista].sort((a, b) => a.mesa - b.mesa || a.agentId.localeCompare(b.agentId))) {
      if (seVa(c)) continue;
      const suyos = m.get(c.projectId);
      if (suyos) suyos.push(c);
      else m.set(c.projectId, [c]);
    }
    return m;
  }, [lista]);
  // El letrero resume lo que le toca a quien mira (letrero.ts): lo de otra persona es de su dueño, como en la lista.
  const resumenSalas = useMemo(
    () => new Map(salas.map((s) => [s.projectId, resumenLetrero(lista.filter((c) => c.projectId === s.projectId && !marcas.get(c.agentId)?.de), santi)])),
    [salas, lista, marcas, santi],
  );
  // «Te necesita» en la escena = el marcador de quien mira (nunca lo de otra persona): indicadores de borde y cámara.
  // Los de una sala plegada no tienen mesa: cuentan en su carpeta (plegar nunca esconde la urgencia; «↓ te necesita» lleva
  // hasta la tarjeta).
  const destacadas = useMemo(() => {
    if (!mundo) return [];
    const enMesa = mundo.mesas.filter((me) => !!me.agentId && !!marcas.get(me.agentId)?.destaca);
    const enCarpeta = mundo.zonas
      .filter((z) => z.plegada)
      .flatMap((z) =>
        lista
          .filter((c) => c.projectId === z.projectId && !seVa(c) && !!marcas.get(c.agentId)?.destaca)
          .map((c): MesaMundo => ({ projectId: z.projectId, grupoId: null, indice: -1, rect: z.rect, agentId: c.agentId, libre: false })),
      );
    return [...enMesa, ...enCarpeta];
  }, [mundo, marcas, lista]);
  useCamara(caja, mundo, panelIzqPx, destacadas[0]);

  // ─── Carpetas: abrir (pliega la anterior), plegar, y llevar la vista al agente abierto en el chat ───
  // Al abrir una carpeta cambia la planta (y la cámara vuelve a su sitio): luego la vista va hasta la sala abierta.
  const salaPorVer = useRef<string | null>(null);
  const desplegar = (projectId: string) => {
    salaPorVer.current = projectId;
    setAbierta(projectId);
    setDesplegando(projectId);
  };
  const plegar = () => setAbierta(null);
  /** Lleva la vista hasta la mesa de un agente si no se ve entera (no la mueve si ya está a la vista). */
  const verMesa = (agentId: string): void => {
    const m = mundoRef.current;
    const el = caja.current;
    const me = m?.mesas.find((x) => x.agentId === agentId);
    if (!m || !el || !me) return;
    const arriba = me.rect.y * m.z;
    const abajo = (me.rect.y + me.rect.h) * m.z;
    if (arriba < el.scrollTop || abajo > el.scrollTop + el.clientHeight) el.scrollTop = Math.max(0, (me.rect.y + me.rect.h / 2) * m.z - el.clientHeight / 2);
  };
  // Si su sala va plegada, se abre y, cuando la planta nueva está pintada, la vista baja hasta su mesa.
  const porEnfocar = useRef<string | null>(null);
  useEffect(() => {
    if (!enfocado) return;
    const c = contratadosRef.current[enfocado];
    if (c && mundoRef.current?.zonas.find((z) => z.projectId === c.projectId)?.plegada) {
      porEnfocar.current = enfocado;
      desplegar(c.projectId);
    } else verMesa(enfocado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enfocado]);
  useLayoutEffect(() => {
    const sala = salaPorVer.current;
    const zona = sala ? mundo?.zonas.find((z) => z.projectId === sala && !z.plegada) : undefined;
    const el = caja.current;
    if (zona && mundo && el) {
      salaPorVer.current = null;
      // Su cabecera arriba, con aire, si la sala no se ve entera (como al abrir una carpeta del iPhone: lo de dentro, a la vista).
      const arriba = zona.letrero.y * mundo.z;
      if (arriba < el.scrollTop || (zona.rect.y + zona.rect.h) * mundo.z > el.scrollTop + el.clientHeight) el.scrollTop = Math.max(0, arriba - PARED * mundo.z);
    }
    const id = porEnfocar.current;
    if (id && mundo && !mundo.zonas.find((z) => z.projectId === contratadosRef.current[id]?.projectId)?.plegada) {
      porEnfocar.current = null;
      verMesa(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mundo]);

  // ─── Grupos: crear, renombrar, deshacer ───
  const nombresEnUso = (projectId: string) => grupos.filter((g) => g.projectId === projectId && lista.some((c) => c.grupoId === g.id)).map((g) => g.nombre);
  /** Crea un grupo con estos agentes (en orden de lectura) y abre su nombre en el cartel, como «Carpeta nueva con la selección». */
  const agrupar = async (projectId: string, ids: readonly string[]) => {
    const m = mundoRef.current;
    const orden = m ? m.mesas.filter((me) => me.agentId && ids.includes(me.agentId)).map((me) => me.agentId!) : [...ids];
    const elegidos = orden.map((id) => contratadosRef.current[id]).filter((c): c is ContratadoDTO => !!c && c.projectId === projectId);
    if (!elegidos.length) return;
    const { nombre, esperaTemaId } = sugerirGrupo(elegidos, contratos, nombresEnUso(projectId));
    try {
      const antes = elegidos.map((c) => ({ agentId: c.agentId, grupoId: c.grupoId ?? null, mesa: c.mesa }));
      const grupoId = await useOficinaStore.getState().crearGrupo(projectId, { nombre, agentIds: elegidos.map((c) => c.agentId), esperaTemaId });
      setEleccion(null);
      setRenombrando({ grupoId, nuevo: true, antes });
      setPropuesta(null);
      // Esperar a Santi es una regla fija (grupos.md §4: «Esperando a Santi» y su contrato): ahí no se pregunta.
      if (esperaTemaId === null)
        void useOficinaStore
          .getState()
          .nombreSugeridoGrupo(grupoId)
          .then((n) => n && setPropuesta({ grupoId, nombre: n }));
    } catch (e) {
      useToastStore.getState().push({ message: mensajeError(e) });
    }
  };
  /** Cancelar un grupo recién creado: se deshace y cada agente vuelve, uno detrás de otro, a donde estaba. */
  const devolverAntes = async (grupoId: string, projectId: string | undefined, antes: readonly { agentId: string; grupoId: string | null; mesa: number }[]) => {
    const store = useOficinaStore.getState();
    try {
      await store.deshacerGrupo(grupoId, false);
      if (!projectId) return;
      for (const a of antes) await store.ordenarMesas(projectId, { accion: 'mover', agentId: a.agentId, grupoId: a.grupoId, mesa: a.mesa });
    } catch (e) {
      useToastStore.getState().push({ message: mensajeError(e) });
    }
  };
  const deshacerGrupo = (grupoId: string) =>
    void useOficinaStore
      .getState()
      .deshacerGrupo(grupoId)
      .catch((e: unknown) => useToastStore.getState().push({ message: mensajeError(e) }));

  // Ctrl/⌘+G agrupa lo elegido (o la mesa con el foco); Ctrl/⌘+Mayús+G deshace su grupo; Esc suelta lo elegido.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && eleccion && !e.defaultPrevented) {
        setEleccion(null);
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.key.toLowerCase() !== 'g' || tecladoOcupado(e)) return;
      e.preventDefault();
      const conFoco = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('[data-agente]')?.dataset.agente;
      const ids = eleccion?.ids.size ? [...eleccion.ids] : conFoco ? [conFoco] : [];
      const primero = ids[0] ? contratadosRef.current[ids[0]] : undefined;
      if (!primero) return;
      if (e.shiftKey) {
        if (primero.grupoId) deshacerGrupo(primero.grupoId);
      } else void agrupar(primero.projectId, ids);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  });

  /** «Mover a ▸» de un agente: los grupos de su sala, «Sin grupo», «Grupo nuevo…» y, con varias elegidas, «Agrupar». */
  const moverA = (c: ContratadoDTO): MoverAGrupo => {
    const enEleccion = eleccion?.projectId === c.projectId && eleccion.ids.has(c.agentId) && eleccion.ids.size > 1;
    return {
      grupos: destinosDeSala(c.projectId, grupos, lista, (id) => !!esperas.get(id)?.conSanti),
      actual: c.grupoId ?? null,
      alMover: (grupoId) => llevarA(c.projectId, c.agentId, { grupoId, mesa: null }),
      alGrupoNuevo: () => void agrupar(c.projectId, enEleccion ? [...eleccion!.ids] : [c.agentId]),
      ...(enEleccion ? { agrupar: { n: eleccion!.ids.size, alAgrupar: () => void agrupar(c.projectId, [...eleccion!.ids]) } } : {}),
    };
  };

  const abrir = (id: string) => {
    tarjeta.cerrar();
    (document.activeElement as HTMLElement | null)?.blur?.();
    alAbrir(id);
  };
  const abrirMenu = (id: string) => {
    tarjeta.cerrar();
    setMenu(id);
  };
  // El menú de un agente: mantener el dedo (iPad) o clic derecho. Fuera de «Ordenar» el dedo sigue desplazando la oficina.
  const pulsacion = usePulsacionLarga(abrirMenu);
  const acciones = useAccionesEstables({
    abrir,
    pulsar: (e: EventoPuntero, c: ContratadoDTO, indice: number) => {
      pulsacion.empezar(e, c.agentId, !ordenando);
      // Con ratón se arrastra siempre; con el dedo, solo en «Ordenar» (fuera, el dedo desplaza la oficina). Con Ctrl/⌘
      // se elige, no se arrastra.
      if (e.ctrlKey || e.metaKey) return;
      empezarArrastre(e, { agentId: c.agentId, projectId: c.projectId, desde: claveSitio({ grupoId: c.grupoId ?? null, mesa: indice }) }, e.pointerType === 'mouse' || ordenando);
    },
    clic: (id: string, mod?: { ctrlKey: boolean; metaKey: boolean }) => {
      if (pulsacion.consumir() || acabaDeSoltar()) return;
      const c = contratadosRef.current[id];
      // Ctrl/⌘+clic elige la mesa (como en el Finder); un clic normal abre el chat y suelta lo elegido.
      if (c && (mod?.ctrlKey || mod?.metaKey)) {
        setEleccion((ant) => {
          const ids = new Set(ant?.projectId === c.projectId ? ant.ids : []);
          if (ids.has(id)) ids.delete(id);
          else ids.add(id);
          return ids.size ? { projectId: c.projectId, ids } : null;
        });
        return;
      }
      setEleccion(null);
      abrir(id);
    },
    teclado: (e: EventoTeclado<HTMLButtonElement>, c: ContratadoDTO, desde: number) => {
      // F2: renombrar (abre su chat con el nombre listo). Tecla de menú o Mayús+F10: su menú.
      if (e.key === 'F2') {
        e.preventDefault();
        useOficinaStore.getState().pedirRenombrar(c.agentId);
        abrir(c.agentId);
        return;
      }
      if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
        e.preventDefault();
        abrirMenu(c.agentId);
        return;
      }
      if (!ordenando || !mundo) return;
      const paso = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!paso) return;
      e.preventDefault();
      const destino = mesaVecina(mundo, c.projectId, desde, paso, c.grupoId ?? null);
      if (destino !== undefined) alOrdenarMesas(c.projectId, { accion: 'mover', agentId: c.agentId, mesa: destino });
    },
    abrirMenu,
    cerrarMenu: () => setMenu(null),
    ordenar: () => setOrdenando(true),
    entrar: (id: string) => !ocupado() && tarjeta.programar(id, 150),
    salir: () => tarjeta.programar(null, 250),
    enfocar: tarjeta.mostrar,
    desenfocar: () => tarjeta.programar(null, 150),
    clicTarjeta: (id: string) => !acabaDeSoltar() && abrir(id),
    retenerTarjeta: tarjeta.retener,
    vistaY: () => caja.current?.scrollTop ?? 0,
  });

  const accionesCartel = useAccionesEstables({
    contratarAqui: (g: GrupoMundo) => alContratar(g.projectId, g.grupoId ?? undefined),
    empezarRenombrar: (grupoId: string) => setRenombrando({ grupoId, nuevo: false }),
    terminarRenombrar: async (grupoId: string, nombre: string | null): Promise<string | null> => {
      const actual = grupos.find((g) => g.id === grupoId);
      const nuevo = renombrando?.grupoId === grupoId && renombrando.nuevo;
      // Esc (o dejarlo vacío) al crear uno: no se crea y cada agente vuelve a SU grupo y SU mesa de antes, como si no hubiera
      // pasado nada (04-11, revisión: antes caían en «Sin grupo» y perdían el grupo del que venían). Al renombrar: queda igual.
      if (nombre === null || !nombre) {
        const antes = nuevo ? (renombrando?.antes ?? []) : [];
        setRenombrando(null);
        if (nuevo) void devolverAntes(grupoId, actual?.projectId, antes);
        return null;
      }
      if (nombre === actual?.nombre) {
        setRenombrando(null);
        return null;
      }
      try {
        await useOficinaStore.getState().editarGrupo(grupoId, { nombre });
        setRenombrando(null);
        return null;
      } catch (e) {
        return e instanceof ApiHttpError && e.code === 'GRUPO_NOMBRE_REPETIDO' ? t('oficinaV2.grupos.repetido', { nombre }) : mensajeError(e);
      }
    },
    aparcarLos: (grupoId: string, aparcar: boolean) => {
      const suyos = lista.filter((c) => c.grupoId === grupoId && (aparcar ? !marcas.get(c.agentId)?.aparcado && quieto(c) : !!marcas.get(c.agentId)?.aparcado));
      for (const c of suyos) void useOficinaStore.getState().aparcar(c.agentId, aparcar, false);
    },
    atar: (grupoId: string, temaId: number | null) =>
      void useOficinaStore.getState().editarGrupo(grupoId, { esperaTemaId: temaId }).catch((e: unknown) => useToastStore.getState().push({ message: mensajeError(e) })),
    deshacer: deshacerGrupo,
    moverOrden: (grupoId: string, paso: 1 | -1) => {
      const g = grupos.find((x) => x.id === grupoId);
      if (!g) return;
      const visibles = mundo?.grupos.filter((x) => x.projectId === g.projectId && x.grupoId).map((x) => x.grupoId!) ?? [];
      const vecino = grupos.find((x) => x.id === visibles[visibles.indexOf(grupoId) + paso]);
      if (vecino) void useOficinaStore.getState().editarGrupo(grupoId, { orden: vecino.orden }).catch((e: unknown) => useToastStore.getState().push({ message: mensajeError(e) }));
    },
    abrirContrato: (temaId: number) => usePelotasStore.getState().abrirSanti(temaId),
    agarrarCartel: (e: EventoPuntero, g: GrupoMundo) => {
      if (e.button !== 0 || !g.grupoId) return;
      const grupoId = g.grupoId;
      const bajo = (x: number, y: number) => {
        const r = capa.current?.getBoundingClientRect();
        const m = mundoRef.current;
        if (!m || !r) return null;
        const clave = sitioEn(m, g.projectId, x - r.left, y - r.top);
        const destino = clave ? sitioDeClave(clave).grupoId : null;
        return destino && destino !== grupoId ? destino : null;
      };
      setCartelArrastrado({ grupoId, sobre: null });
      const mover = (ev: PointerEvent) => setCartelArrastrado({ grupoId, sobre: bajo(ev.clientX, ev.clientY) });
      const soltar = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', mover);
        window.removeEventListener('pointerup', soltar);
        window.removeEventListener('pointercancel', soltar);
        setCartelArrastrado(null);
        const sobre = ev.type === 'pointerup' ? bajo(ev.clientX, ev.clientY) : null;
        const destino = sobre ? grupos.find((x) => x.id === sobre) : undefined;
        if (destino) void useOficinaStore.getState().editarGrupo(grupoId, { orden: destino.orden }).catch((er: unknown) => useToastStore.getState().push({ message: mensajeError(er) }));
      };
      window.addEventListener('pointermove', mover);
      window.addEventListener('pointerup', soltar);
      window.addEventListener('pointercancel', soltar);
    },
  });

  // Los monigotes que se pintan en el DOM y no en el lienzo: el que va colgando del puntero y, en «Ordenar», todos los
  // sentados (tiemblan enteros, como los iconos del iPhone).
  // Depende de QUIÉN se arrastra, no del arrastre (que cambia en cada fotograma): el lienzo no se repinta al mover.
  const arrastrando = arrastre?.agentId ?? null;
  const enDom = useMemo(() => {
    const ids = new Set<string>();
    if (arrastrando) ids.add(arrastrando);
    if (ordenando) for (const me of mundo?.mesas ?? []) if (me.agentId) ids.add(me.agentId);
    return ids;
  }, [arrastrando, ordenando, mundo]);
  const centroX = panelIzqPx + (hueco.w - panelIzqPx) / 2;
  const sobreSitio = arrastre?.sobre ?? null;
  const esDestino = (projectId: string, grupoId: string | null, indice: number) =>
    !!arrastre && arrastre.projectId === projectId && sobreSitio === claveSitio({ grupoId, mesa: indice }) && arrastre.desde !== sobreSitio;
  const placaDestino = (g: GrupoMundo) =>
    (!!arrastre && arrastre.projectId === g.projectId && sobreSitio === claveSitio({ grupoId: g.grupoId, mesa: null })) || (!!g.grupoId && cartelArrastrado?.sobre === g.grupoId);
  const miembrosDe = (grupoId: string) => lista.filter((c) => c.grupoId === grupoId);
  return (
    // `isolate`: el mundo es una capa propia. Sus tarjetas y avisos (z-40 dentro) se ordenan solo entre ellos y nunca se
    // pintan encima de la lista ni del chat que el marco pone por encima (06-10, iPad de Pau).
    <div ref={marco} className="absolute inset-0 isolate">
      <div ref={caja} className={cn('absolute inset-0 bg-oficina-fondo [scrollbar-width:thin]', mundo?.desborda ? cn('overflow-y-auto', mundo.ancho * mundo.z - hueco.w >= mundo.z ? 'overflow-x-auto' : 'overflow-x-hidden') : 'overflow-hidden')}>
        {mundo ? (
          <div ref={capa} className={cn('relative', (arrastre || cartelArrastrado) && 'cursor-grabbing select-none')} style={{ width: mundo.ancho * mundo.z, height: mundo.alto * mundo.z }}>
            <Lienzo mundo={mundo} figuras={figuras} enDom={enDom} />
            {mundo.zonas.map((zona) => (
              <Letrero
                key={zona.projectId}
                zona={zona}
                z={mundo.z}
                // Plegada, lo urgente va en la insignia de su carpeta (no dos veces).
                resumen={zona.plegada ? undefined : resumenSalas.get(zona.projectId)}
                alAjustarSala={alAjustarSala}
                alPlegar={zona.abierta ? plegar : undefined}
              />
            ))}
            {mundo.zonas.map((zona) =>
              zona.plegada ? (
                <Carpeta
                  key={`carpeta-${zona.projectId}`}
                  zona={zona}
                  z={mundo.z}
                  gente={gentePorSala.get(zona.projectId) ?? SIN_GENTE}
                  marcas={marcas}
                  resumen={resumenSalas.get(zona.projectId)}
                  resaltada={!!resaltado && contratados[resaltado]?.projectId === zona.projectId}
                  alAbrir={desplegar}
                  alContratar={alContratar}
                />
              ) : null,
            )}
            {mundo.grupos.map((g) => {
              if (!g.grupoId) return <EtiquetaSinGrupo key={`sin-${g.projectId}`} g={g} z={mundo.z} destino={placaDestino(g)} />;
              const espera = esperas.get(g.grupoId);
              const suyos = miembrosDe(g.grupoId);
              return (
                <CartelGrupo
                  key={g.grupoId}
                  g={{ ...g, grupoId: g.grupoId }}
                  z={mundo.z}
                  espera={espera}
                  duenoSeFue={!!espera?.contrato.agentId && !contratados[espera.contrato.agentId]}
                  contratos={contratos}
                  aparcables={suyos.filter((c) => !marcas.get(c.agentId)?.aparcado && quieto(c) && !santi.get(c.agentId)?.conSanti).length}
                  dormidos={suyos.filter((c) => marcas.get(c.agentId)?.aparcado).length}
                  ordenando={ordenando}
                  renombrando={renombrando?.grupoId === g.grupoId}
                  propuesta={renombrando?.nuevo && renombrando.grupoId === g.grupoId && propuesta?.grupoId === g.grupoId ? propuesta.nombre : undefined}
                  destino={placaDestino(g)}
                  acciones={accionesCartel}
                />
              );
            })}
            {/* Esperando a Santi (regla 9): la isla entera, apenas teñida de cian; sus agentes, apagados en su mesa. */}
            {mundo.grupos.map((g) =>
              g.grupoId && esperas.get(g.grupoId)?.conSanti ? (
                <div key={`santi-${g.grupoId}`} className="pointer-events-none absolute rounded-o-s bg-oficina-santi/5" style={estiloDe(g.placa, mundo.z)} data-testid="placa-santi" aria-hidden />
              ) : null,
            )}
            <Salida mundo={mundo} contratados={contratados} alAbrir={acciones.abrir} />
            {mundo.mesas.map((me) => {
              const clave = `${me.projectId}#${me.grupoId ?? ''}#${me.indice}`;
              const sala = nombreSala.get(me.projectId) ?? '';
              const aqui = ordenando;
              const c = me.agentId ? contratados[me.agentId] : undefined;
              const destino = esDestino(me.projectId, me.grupoId, me.indice);
              if (!c && aqui) return <MesaVacia key={clave} me={me} z={mundo.z} sala={sala} destino={destino} alContratar={alContratar} />;
              if (!c && me.libre) return <MesaLibre key={clave} me={me} z={mundo.z} sala={sala} destino={destino} alContratar={alContratar} />;
              if (!c) return destino ? <MarcaDestino key={`destino-${clave}`} ocupada={false} estilo={estiloDe(me.rect, mundo.z)} /> : null;
              return (
                <MesaAgente
                  key={c.agentId}
                  c={c}
                  m={marcas.get(c.agentId)!}
                  me={me}
                  z={mundo.z}
                  sala={nombreSala.get(c.projectId) ?? ''}
                  ordenando={aqui}
                  arrastrado={arrastre?.agentId === c.agentId}
                  destino={destino}
                  resaltado={resaltado === c.agentId}
                  elegida={!!eleccion?.ids.has(c.agentId)}
                  conTarjeta={tarjeta.abierta === c.agentId || resaltado === c.agentId}
                  conMenu={menu === c.agentId}
                  // Solo la mesa con el menú abierto recibe sus destinos (un objeto nuevo no repinta a las demás).
                  menuGrupos={menu === c.agentId ? moverA(c) : undefined}
                  acciones={acciones}
                />
              );
            })}
            {/* Al abrir una carpeta, la sala se despliega de arriba abajo (250 ms, curva de Apple): una cortina del color del
                suelo que se recoge. Con «reducir movimiento», no hay cortina. */}
            {desplegando
              ? mundo.zonas
                  .filter((zona) => zona.projectId === desplegando && !zona.plegada)
                  .map((zona) => (
                    <div
                      key={`despliegue-${zona.projectId}`}
                      className="pointer-events-none absolute motion-safe:animate-desplegar motion-reduce:hidden"
                      style={{ ...estiloDe({ x: zona.letrero.x, y: zona.rect.y, w: zona.rect.w, h: zona.rect.h }, mundo.z), background: escena.suelo }}
                      onAnimationEnd={() => setDesplegando(null)}
                      data-testid="despliegue"
                      aria-hidden
                    />
                  ))
              : null}
            {arrastre && contratados[arrastre.agentId] ? <Fantasma c={contratados[arrastre.agentId]!} z={mundo.z} x={arrastre.px} y={arrastre.py} /> : null}
          </div>
        ) : null}
      </div>
      {ordenando ? <BarraOrdenar centroX={centroX} alListo={() => setOrdenando(false)} /> : null}
      <IndicadoresFuera caja={caja} mundo={mundo} destacadas={destacadas} alto={hueco.h} centroX={centroX} conBarra={ordenando} />
    </div>
  );
});

/** Lo que trabaja o se va no se aparca (no está quieto). */
const quieto = (c: ContratadoDTO) => !['trabajando', 'llegando', 'cerrando', 'saliendo'].includes(c.situacion);
